import { Badge } from '@/components/ui/badge'
import { EmptyState } from '@/components/patterns/empty-state'
import { Card, CardContent } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import dayjs from '@/lib/dayjs'
import { usePriceAdjustmentTimeline } from '@/lib/services/price-adjustments/priceAdjustments.query'
import { cn } from '@/lib/utils'
import { getPriceAdjustmentPriorityValue, priceAdjustmentIsPriority } from './-price-change-board'

const TIMELINE_LANES = [
  { key: 'collections', label: 'Collections' },
  { key: 'global', label: 'All Products' },
]

const TIMELINE_MIN_WINDOW_WIDTH_PERCENT = 2
const TIMELINE_WINDOW_GAP_PERCENT = 1.5
const TIMELINE_LANE_MIN_HEIGHT = 48
const TIMELINE_ROW_HEIGHT = 42
const TIMELINE_WINDOW_HEIGHT = 32

const clampPercent = (value) => Math.min(100, Math.max(0, value))

const getTimestamp = (value) => {
  const parsed = value ? dayjs(value) : null

  return parsed?.isValid() ? parsed.valueOf() : null
}

const formatTimelineDate = (value) => {
  const parsed = value ? dayjs(value) : null

  return parsed?.isValid() ? parsed.format('MMM D') : null
}

const formatTimelineEndDate = (value) => {
  const parsed = value ? dayjs(value) : null

  return parsed?.isValid() ? parsed.subtract(1, 'day').format('MMM D') : null
}

const formatTimelineRange = (window) => {
  if (window?.starts_before_range && window?.ends_after_range) {
    return 'Runs through the next 12 months'
  }

  if (window?.starts_before_range) {
    return `Active through ${formatTimelineEndDate(window.visible_ends_at)}`
  }

  if (window?.ends_after_range) {
    return `${formatTimelineDate(window.visible_starts_at)} through the next 12 months`
  }

  return `${formatTimelineDate(window.visible_starts_at)} - ${formatTimelineEndDate(
    window.visible_ends_at
  )}`
}

const getTimelinePositionPercent = (value, range) => {
  const rangeStart = getTimestamp(range?.starts_at)
  const rangeEnd = getTimestamp(range?.ends_at)
  const timestamp = getTimestamp(value)

  if (rangeStart === null || rangeEnd === null || timestamp === null || rangeEnd <= rangeStart) {
    return 0
  }

  return clampPercent(((timestamp - rangeStart) / (rangeEnd - rangeStart)) * 100)
}

const getTimelineWindowBounds = (window, range) => {
  const rangeStart = getTimestamp(range?.starts_at)
  const rangeEnd = getTimestamp(range?.ends_at)
  const windowStart = getTimestamp(window?.visible_starts_at)
  const windowEnd = getTimestamp(window?.visible_ends_at)

  if (
    rangeStart === null ||
    rangeEnd === null ||
    windowStart === null ||
    windowEnd === null ||
    rangeEnd <= rangeStart
  ) {
    return { left: 0, right: 0, width: 0 }
  }

  const rawLeft = getTimelinePositionPercent(window?.visible_starts_at, range)
  const rawRight = getTimelinePositionPercent(window?.visible_ends_at, range)
  const width = Math.min(
    100,
    Math.max(TIMELINE_MIN_WINDOW_WIDTH_PERCENT, Math.max(2, rawRight - rawLeft))
  )
  const left = Math.min(rawLeft, 100 - width)

  return {
    left,
    right: left + width,
    width,
  }
}

const getTimelineWindowStyle = (window) => ({
  left: `${window.timelineBounds?.left ?? 0}%`,
  width: `${window.timelineBounds?.width ?? 0}%`,
})

const getTimelineLaneHeight = (rowCount) =>
  Math.max(TIMELINE_LANE_MIN_HEIGHT, rowCount * TIMELINE_ROW_HEIGHT)

const getTimelineWindowTop = (timelineRow, laneRowHeight) =>
  timelineRow * laneRowHeight + (laneRowHeight - TIMELINE_WINDOW_HEIGHT) / 2

const timelineWindowIsActive = (window) => {
  if (window?.status !== 'active') {
    return false
  }

  const windowStart = getTimestamp(window?.starts_at)
  const windowEnd = getTimestamp(window?.ends_at)
  const now = dayjs().valueOf()

  if (windowStart === null) {
    return false
  }

  return windowStart <= now && (windowEnd === null || windowEnd > now)
}

const getTimelineWindowCurrentKey = (window) =>
  `${Number(window?.id ?? 0)}:${window?.starts_at ?? ''}`

const getTimelineWindowCollectionIds = (window) => {
  const collectionIds = Array.isArray(window?.collection_ids)
    ? window.collection_ids
    : (window?.collections ?? []).map((collection) => collection?.id)

  return collectionIds.map(Number).filter((collectionId) => collectionId > 0)
}

const getLatestStartedTimelineWindow = (windows) =>
  [...windows].sort((left, right) => {
    const priorityDifference =
      getPriceAdjustmentPriorityValue(right) - getPriceAdjustmentPriorityValue(left)

    if (priorityDifference !== 0) {
      return priorityDifference
    }

    const timestampDifference =
      (getTimestamp(right?.starts_at) ?? 0) - (getTimestamp(left?.starts_at) ?? 0)

    if (timestampDifference !== 0) {
      return timestampDifference
    }

    return Number(right?.id ?? 0) - Number(left?.id ?? 0)
  })[0] ?? null

const getCurrentTimelineWindowIds = (laneKey, windows) => {
  const activeWindows = windows.filter(timelineWindowIsActive)

  if (activeWindows.length === 0) {
    return new Set()
  }

  if (laneKey === 'global') {
    const winner = getLatestStartedTimelineWindow(activeWindows)

    return new Set(winner ? [getTimelineWindowCurrentKey(winner)] : [])
  }

  const collectionIds = [
    ...new Set(activeWindows.flatMap((window) => getTimelineWindowCollectionIds(window))),
  ]

  return collectionIds.reduce((currentWindowIds, collectionId) => {
    const winner = getLatestStartedTimelineWindow(
      activeWindows.filter((window) =>
        getTimelineWindowCollectionIds(window).includes(collectionId)
      )
    )

    if (winner) {
      currentWindowIds.add(getTimelineWindowCurrentKey(winner))
    }

    return currentWindowIds
  }, new Set())
}

const timelineWindowsCollide = (left, right) => {
  return (
    left.left < right.right + TIMELINE_WINDOW_GAP_PERCENT &&
    left.right + TIMELINE_WINDOW_GAP_PERCENT > right.left
  )
}

const getTimelinePriority = (window) => {
  if (window?.starts_before_range && window?.ends_after_range) {
    return 0
  }

  if (window?.ends_after_range || !window?.ends_at) {
    return 1
  }

  return 2
}

const assignTimelineRows = (windows, range) => {
  const rowBounds = []

  // Assign rows by visual bounds and pricing priority. Temporary/effective windows claim
  // upper rows first; long-running base rules fall underneath the overrides they support.
  return [...windows]
    .map((window) => ({
      ...window,
      timelineBounds: getTimelineWindowBounds(window, range),
    }))
    .sort((left, right) => {
      const priorityComparison = getTimelinePriority(right) - getTimelinePriority(left)

      if (priorityComparison !== 0) {
        return priorityComparison
      }

      const startsAtComparison =
        (getTimestamp(right?.starts_at) ?? 0) - (getTimestamp(left?.starts_at) ?? 0)

      if (startsAtComparison !== 0) {
        return startsAtComparison
      }

      const widthComparison = left.timelineBounds.width - right.timelineBounds.width

      if (widthComparison !== 0) {
        return widthComparison
      }

      return Number(left?.id ?? 0) - Number(right?.id ?? 0)
    })
    .map((window) => {
      const row = rowBounds.findIndex((bounds) => {
        return !bounds.some((existingBounds) => {
          return timelineWindowsCollide(existingBounds, window.timelineBounds)
        })
      })
      const assignedRow = row === -1 ? rowBounds.length : row

      rowBounds[assignedRow] = [...(rowBounds[assignedRow] ?? []), window.timelineBounds]

      return {
        ...window,
        timelineRow: assignedRow,
      }
    })
}

export function PriceChangeTimelinePanel({ onHighlightAdjustments, onClose }) {
  const { data: timeline, isFetching } = usePriceAdjustmentTimeline({ enabled: true })
  const windows = timeline?.windows ?? []
  const range = timeline?.range

  const handleSelectWindow = (adjustmentId) => {
    onHighlightAdjustments?.([adjustmentId])
    onClose?.()
  }

  if (isFetching && windows.length === 0) {
    return <Typography variant="supporting">Loading timeline...</Typography>
  }

  if (windows.length === 0) {
    return <EmptyState size="compact" title="No price changes scheduled for the next 12 months." />
  }

  return (
    <div className="space-y-4 rounded-xl bg-card/95 p-4">
      <div className="space-y-1">
        <Typography variant="h4">Full pricing timeline</Typography>
        <Typography variant="supporting">
          Next 12 months of scheduled price changes. Any bar with the diamond indicator is active
          right now.
        </Typography>
      </div>
      <div className="space-y-6 pb-1">
        <TimelineScale range={range} />
        {TIMELINE_LANES.map((lane) => (
          <TimelineLane
            key={lane.key}
            lane={lane}
            range={range}
            windows={windows.filter((window) => window.scope_type === lane.key)}
            onSelectWindow={handleSelectWindow}
          />
        ))}
      </div>
    </div>
  )
}

function TimelineScale({ range }) {
  const startsAt = dayjs(range?.starts_at)
  const endsAt = dayjs(range?.ends_at)

  if (!startsAt.isValid() || !endsAt.isValid()) {
    return null
  }

  const months = []
  let cursor = startsAt.startOf('month').isBefore(startsAt)
    ? startsAt.startOf('month').add(1, 'month')
    : startsAt.startOf('month')

  while (cursor.isBefore(endsAt)) {
    months.push(cursor)
    cursor = cursor.add(1, 'month')
  }

  return (
    <div className="grid grid-cols-1 gap-2">
      <div className="relative h-4 text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
        {months.map((month) => (
          <span
            key={month.format('YYYY-MM')}
            className="absolute top-0 -translate-x-1/2"
            style={{ left: `${getTimelinePositionPercent(month, range)}%` }}
          >
            {month.format('MMM')}
          </span>
        ))}
      </div>
      <div className="relative h-4">
        <div className="absolute inset-x-0 top-1/2 h-px bg-border/50" />
        <span
          aria-label="Today"
          data-testid="timeline-today-marker"
          role="img"
          className="absolute top-1/2 left-0 size-3 -translate-x-1/2 -translate-y-1/2 rotate-45 rounded-[3px] border border-background bg-info shadow-sm ring-2 ring-info/25"
        />
      </div>
    </div>
  )
}

function TimelineLane({ lane, range, windows, onSelectWindow }) {
  const rowedWindows = assignTimelineRows(windows, range)
  const currentWindowIds = getCurrentTimelineWindowIds(lane.key, rowedWindows)
  const rowCount = Math.max(1, ...rowedWindows.map((window) => window.timelineRow + 1))
  const laneHeight = getTimelineLaneHeight(rowCount)
  const laneRowHeight = laneHeight / rowCount

  return (
    <section className="space-y-2">
      <Typography variant="eyebrow" data-testid={`timeline-lane-label-${lane.key}`}>
        {lane.label}
      </Typography>
      <div className="relative rounded-xl bg-muted/20" style={{ height: `${laneHeight}px` }}>
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-1 left-0 z-10 border-l-2 border-info/70"
        />
        {rowedWindows.length === 0 ? (
          <Typography variant="caption" className="absolute top-3 left-4">
            {lane.key === 'global' ? 'No all-product changes.' : 'No collection changes.'}
          </Typography>
        ) : (
          rowedWindows.map((window) => (
            <TimelineWindowBar
              key={`${window.id}-${window.starts_at}`}
              range={range}
              window={{
                ...window,
                is_current: currentWindowIds.has(getTimelineWindowCurrentKey(window)),
              }}
              laneRowHeight={laneRowHeight}
              onSelectWindow={onSelectWindow}
            />
          ))
        )}
      </div>
    </section>
  )
}

function TimelineWindowBar({ laneRowHeight, range, window, onSelectWindow }) {
  const isDecrease = window.operation === 'decrease'
  const isCurrent = window.is_current === true

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-raw-button-owner="price-change-timeline-window"
          aria-label={`${window.title} ${window.signed_amount}${isCurrent ? ' Current price change' : ''} ${formatTimelineRange(window)}`}
          data-timeline-window={window.id}
          data-active-now={isCurrent ? 'true' : undefined}
          className={cn(
            'absolute inline-flex h-8 min-w-0 items-center justify-center overflow-hidden rounded-lg border px-1 shadow-sm transition-colors',
            isDecrease
              ? 'border-selection-border bg-accentSecondary text-interaction hover:bg-accentSecondary/80'
              : 'border-status-success-border bg-status-success-background text-status-success-foreground hover:bg-status-success-background/80',
            isCurrent && 'z-20 ring-2 ring-info/65 ring-offset-2 ring-offset-background'
          )}
          style={{
            ...getTimelineWindowStyle(window, range),
            top: `${getTimelineWindowTop(window.timelineRow, laneRowHeight)}px`,
          }}
          onClick={() => onSelectWindow(window.id)}
        >
          {isCurrent ? (
            <span
              aria-hidden="true"
              data-testid="timeline-current-marker"
              className="shrink-0 size-2.5 rotate-45 rounded-[2px] bg-info shadow-sm ring-2 ring-info/20"
            />
          ) : null}
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        variant="secondary"
        className="max-w-xs p-3 text-card-foreground ring-1 ring-foreground/10"
      >
        <div className="space-y-2">
          <div>
            <p className="font-semibold text-foreground">{window.title}</p>
            {window.supporting_title ? (
              <p className="text-[11px] text-muted-foreground">{window.supporting_title}</p>
            ) : null}
            {priceAdjustmentIsPriority(window) ? (
              <Badge variant="info" size="sm" className="mt-2">
                Priority
              </Badge>
            ) : null}
          </div>
          <div className="flex items-center justify-between gap-4 text-xs">
            <span className="text-muted-foreground">Change</span>
            <span
              className={cn(
                'font-semibold',
                isDecrease ? 'text-interaction' : 'text-status-success-foreground'
              )}
            >
              {window.signed_amount}
            </span>
          </div>
          <div className="flex items-center justify-between gap-4 text-xs">
            <span className="text-muted-foreground">Window</span>
            <span className="text-right text-foreground">{formatTimelineRange(window)}</span>
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}
